import { AkismetGraph } from "components/AkismetData";
import ChangeCard from "components/ChangeCard";
import MailchimpCampaignCard from "components/dashboard/MailchimpCampaignCard";
import WooCommerceProductsTable from "components/dashboard/WooCommerceProductsTable";
import { GSCMetricsCards } from "components/GSCMetricsCards";
import CountdownInsights from "components/insights/InsightsOverview";
import { Loading } from "components/Loading";
import SectionHeading from "components/text/SectionHeading";
import { ICampaigns, MailchimpAnalysis } from "interfaces/ICampaigns.interface";
import { IGAMetrics } from "interfaces/IGA.interface";
import { SendGridStatItem } from "interfaces/ISendGrid.interafce";
import { IWooMetrics } from "interfaces/IWoo.interface";
import { IWordpress } from "interfaces/IWordpress.interface";
import { Activity, Calendar, Package, RefreshCw, Wrench } from "lucide-react";
import { useAuth } from "providers/auth.provider";
import { useSite } from "providers/site.provider";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  getAkismetData,
  getCampaignsData,
  getGaData,
  getGoogleSearchConsoleData,
  getSendGridData,
  getWooData,
  getWordpressData,
} from "services/apf-api.service";
import SideMenu from "../components/SideMenu";
import ThemeToggle from "../components/ThemeToggle";
import { applyDarkClass, applySiteTheme, getStoredTheme } from "../theme";
import { WordpressSection } from "components/Wordpress/WordpressSection";

export default function Dashboard() {
  const { selectedSite } = useSite();
  const { idToken } = useAuth();

  const [integrations, setIntegrations] = useState<string[]>(
    selectedSite?.integrations || []
  );
  const [gaMetrics, setGaMetrics] = useState<IGAMetrics | null>(null);
  const [woo, setWooMetrics] = useState<IWooMetrics | null>(null);
  const [campaigns, setCampaigns] = useState<MailchimpAnalysis | null>(null);
  const [wordpress, setWordpress] = useState<IWordpress | null>(null);
  const [sendGrid, setSendGrid] = useState<SendGridStatItem[] | null>(null);
  const [googleSearchConsole, setGoogleSearchConsole] = useState<any>(null);
  const [akismet, setAkismet] = useState<any>(null);

  const [loading, setLoading] = useState(false);

  // Date range state
  const [range, setRange] = useState<string>("30");
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState<string>(
    () => new Date().toISOString().split("T")[0]
  );

  // Sync range → start/end dates
  useEffect(() => {
    if (range) {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - parseInt(range));
      setStartDate(start.toISOString().split("T")[0]);
      setEndDate(end.toISOString().split("T")[0]);
    }
  }, [range]);

  const formatDate = (date: Date) => date.toLocaleDateString();

  useEffect(() => {
    if (selectedSite?.id) {
      try {
        localStorage.setItem("lastSiteId", selectedSite.id);
      } catch {}
    }
  }, [selectedSite?.id]);

  useEffect(() => {
    const mode = getStoredTheme();
    applyDarkClass(mode);
    applySiteTheme(selectedSite?.id, mode === "dark");
  }, [selectedSite?.id]);

  // Fetch data whenever site, date range, or integrations change
  useEffect(() => {
    if (!selectedSite?.id || !selectedSite.integrations) return;
    if (new Date(endDate) < new Date(startDate)) return;

    setLoading(true);
    const fetchData = async () => {
      const startStr = startDate;
      const endStr = endDate;

      const promises: Promise<void>[] = [];

      if (selectedSite.integrations.includes("ga")) {
        promises.push(
          getGaData(selectedSite.id, startStr, endStr, idToken).then(
            setGaMetrics
          )
        );
      }
      if (selectedSite.integrations.includes("wc")) {
        promises.push(
          getWooData(selectedSite.id, startStr, endStr, idToken).then(
            setWooMetrics
          )
        );
      }
      if (selectedSite.integrations.includes("mc")) {
        promises.push(
          getCampaignsData(selectedSite.id, startStr, endStr, idToken).then(
            setCampaigns
          )
        );
      }
      if (selectedSite.integrations.includes("wp")) {
        promises.push(
          getWordpressData(selectedSite.id, idToken).then(setWordpress)
        );
      }
      if (selectedSite.integrations.includes("sg")) {
        promises.push(
          getSendGridData(selectedSite.id, startStr, endStr, idToken).then(
            setSendGrid
          )
        );
      }
      if (selectedSite.integrations.includes("gsc")) {
        promises.push(
          getGoogleSearchConsoleData(
            selectedSite.id,
            startStr,
            endStr,
            idToken
          ).then(setGoogleSearchConsole)
        );
      }
      if (selectedSite.integrations.includes("aks")) {
        promises.push(
          getAkismetData(selectedSite.id, startStr, endStr, idToken).then(
            setAkismet
          )
        );
      }

      await Promise.all(promises);
      setIntegrations(selectedSite.integrations);
      setLoading(false);
    };

    fetchData();
  }, [selectedSite, startDate, endDate, idToken]);

  // Loading timer

  if (!integrations || !selectedSite)
    return <p>No integrations found for client</p>;

  const hasIntegration = (key: string) => integrations.includes(key);

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-[240px_1fr]">
      <SideMenu />
      <main className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm text-muted-foreground">
              Dashboard | {formatDate(new Date(startDate))} to{" "}
              {formatDate(new Date(endDate))}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {selectedSite.name}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="rounded-md border bg-background px-3 py-1.5 text-sm"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setRange(""); // reset quick range
              }}
              className="rounded-md border bg-background px-3 py-1.5 text-sm"
            />
            <span className="mx-1 text-sm text-muted-foreground">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setRange(""); // reset quick range
              }}
              className="rounded-md border bg-background px-3 py-1.5 text-sm"
            />
          </div>
        </div>

        <CountdownInsights items={null} />
        <SectionHeading
          title="Summary"
          subtitle="Your key metrics, summarised, for the selected time period"
        />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-6">
          <ChangeCard
            metric={gaMetrics?.websiteTraffic?.sessions}
            change={gaMetrics?.websiteTraffic?.change}
            yoyChange={undefined}
            label="Website Traffic"
            tooltip={gaMetrics?.websiteTraffic?.tooltip}
          />
          <ChangeCard
            metric={gaMetrics?.organicTraffic?.sessions}
            change={gaMetrics?.organicTraffic?.change}
            yoyChange={undefined}
            label="Organic Traffic"
            tooltip={gaMetrics?.organicTraffic?.tooltip}
          />
          <ChangeCard
            metric={woo?.revenue?.amount}
            change={woo?.revenue?.change}
            yoyChange={woo?.revenue?.yoyChange}
            label="Revenue"
            tooltip="Total revenue from WooCommerce orders. Note: This may not match your WooCommerce dashboard exactly due to differences in when data was fetched and order statuses."
            prefix="£"
          />
          <ChangeCard
            metric={woo?.orders?.count}
            change={woo?.orders?.change}
            yoyChange={woo?.orders?.yoyChange}
            label="Orders"
            tooltip="Total number of WooCommerce orders. Note: This may not match your WooCommerce dashboard exactly due to differences in when data was fetched and order statuses."
          />
          <ChangeCard
            metric={campaigns?.summary.totalSubscribers}
            change={undefined}
            yoyChange={undefined}
            label="Mailchimp Audience"
            tooltip="Number of people in the default Mailchimp audience."
          />
          <ChangeCard
            metric={campaigns?.campaigns.length}
            change={undefined}
            yoyChange={undefined}
            label="Mailchimp Campaigns"
            tooltip="Number of Mailchimp campaigns sent in the selected time period."
          />
          <ChangeCard
            metric={
              googleSearchConsole &&
              googleSearchConsole.reduce(
                (sum, row) => sum + row.impressions.value,
                0
              )
            }
            label="Impressions"
            tooltip="Total impressions for selected period"
            change={undefined}
            yoyChange={undefined}
          />
          <ChangeCard
            metric={parseFloat(
              googleSearchConsole && googleSearchConsole.length > 0
                ? (
                    googleSearchConsole.reduce(
                      (sum, row) => sum + row.position.value,
                      0
                    ) / googleSearchConsole.length
                  ).toFixed(2)
                : "0.00"
            )}
            label="Avg. Position"
            tooltip="Average search result position"
            change={undefined}
            yoyChange={undefined}
          />
        </div>
        {hasIntegration("wc") && (
          <>
            <SectionHeading
              title="Revenue"
              subtitle="Revenue and orders over time"
            />
            {/* Combined Revenue & Orders Chart - full width */}
            <div className="mb-6 rounded-lg border bg-card p-4 shadow-sm w-full">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-medium">Revenue & Orders</div>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={
                      woo?.revenueData && woo?.ordersData
                        ? woo?.revenueData?.map((d, i) => ({
                            day: d.date,
                            revenue: d.revenue,
                            orders: woo.ordersData[i]?.orderCount ?? 0,
                          }))
                        : []
                    }
                    margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="day"
                      stroke="#9ca3af"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      yAxisId="left"
                      stroke="#9ca3af"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) =>
                        `£${Math.round(value / 100) * 100}`
                      }
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="#9ca3ac"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) =>
                        name === "revenue"
                          ? `£${value.toLocaleString()}`
                          : value
                      }
                      labelFormatter={(label: string) =>
                        label.charAt(0).toUpperCase() + label.slice(1)
                      }
                    />
                    <Bar
                      yAxisId="right"
                      dataKey="orders"
                      fill="hsl(var(--chart-bar))"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="revenue"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Revenue and Orders charts side by side */}
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              {/* Revenue Line Chart */}
              <div className="rounded-lg border bg-card p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-medium">Revenue</div>
                  <div className="text-sm text-muted-foreground">
                    Total £{woo?.revenue?.amount.toLocaleString()}
                  </div>
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={woo?.revenueData?.map((d) => ({
                        day: d.date,
                        revenue: d.revenue,
                      }))}
                      margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="day"
                        stroke="#9ca3af"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="#9ca3af"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) =>
                          `£${Math.round(value / 100) * 100}`
                        }
                        interval="preserveStartEnd"
                      />
                      <Tooltip
                        formatter={(value: number) =>
                          `£${value.toLocaleString()}`
                        }
                        labelFormatter={(label: string) =>
                          label.charAt(0).toUpperCase() + label.slice(1)
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Orders Bar Chart */}
              <div className="rounded-lg border bg-card p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-medium">Orders</div>
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={woo?.ordersData?.map((d) => ({
                        day: d.date,
                        orders: d.orderCount,
                      }))}
                      margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="day"
                        stroke="#9ca3af"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="#9ca3af"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip />
                      <Bar
                        dataKey="orders"
                        fill="hsl(var(--chart-bar))"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <WooCommerceProductsTable woo={woo} />
          </>
        )}

        {(hasIntegration("wp") || hasIntegration("ga")) && (
          <div className="mt-6 grid gap-4 xl:grid-cols-4">
            {hasIntegration("wp") && <WordpressSection wordpress={wordpress} />}

            {hasIntegration("ga") && (
              <div className="rounded-lg border bg-card p-4 shadow-sm xl:col-span-2">
                <SectionHeading
                  title="Google Analytics"
                  subtitle="Take a deeper look at your Google Analytics data"
                />
                <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                  <Activity className="h-4 w-4" /> Google Analytics
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {gaMetrics &&
                    Object.keys(gaMetrics)
                      .sort((a, b) => a.localeCompare(b))
                      .map((key) => {
                        const metricObj = (gaMetrics as any)[key];
                        if (
                          metricObj &&
                          typeof metricObj === "object" &&
                          "sessions" in metricObj
                        ) {
                          return (
                            <ChangeCard
                              key={key}
                              metric={metricObj.sessions}
                              change={metricObj.change}
                              yoyChange={
                                metricObj.changeVsLastYear ?? undefined
                              }
                              label={key
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                              tooltip={metricObj.tooltip}
                            />
                          );
                        }
                        return null;
                      })}
                </div>
              </div>
            )}
          </div>
        )}
        {hasIntegration("gsc") && (
          <GSCMetricsCards data={googleSearchConsole} />
        )}
        {hasIntegration("aks") && <AkismetGraph data={akismet} />}
        {hasIntegration("mc") && (
          <MailchimpCampaignCard campaigns={campaigns?.campaigns || []} />
        )}
        {hasIntegration("sg") && (
          <div className="mt-6 rounded-lg border bg-card p-4 shadow-sm">
            <SectionHeading
              title="SendGrid"
              subtitle="Your SendGrid email statistics"
            />
            {sendGrid
              ?.sort(
                (a, b) =>
                  new Date(b.date).getTime() - new Date(a.date).getTime()
              )
              .map((item) =>
                // if all metrics are zero, skip
                item.stats.reduce(
                  (sum, stat) =>
                    sum +
                    Object.values(stat.metrics).reduce((a, b) => a + b, 0),
                  0
                ) === 0 ? null : (
                  <div key={item.date} className="mb-4">
                    <h3 className="text-sm font-medium mb-2">
                      Date: {new Date(item.date).toLocaleDateString()}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="rounded-md border p-3">
                        <div className="text-sm text-muted-foreground">
                          Emails Sent
                        </div>
                        <div className="mt-1 text-lg font-medium">
                          {item.stats.reduce(
                            (sum, stat) => sum + stat.metrics.delivered,
                            0
                          )}
                        </div>
                      </div>
                      <div className="rounded-md border p-3">
                        <div className="text-sm text-muted-foreground">
                          Opens
                        </div>
                        <div className="mt-1 text-lg font-medium">
                          {item.stats.reduce(
                            (sum, stat) => sum + stat.metrics.opens,
                            0
                          )}
                        </div>
                      </div>
                      <div className="rounded-md border p-3">
                        <div className="text-sm text-muted-foreground">
                          Clicks
                        </div>
                        <div className="mt-1 text-lg font-medium">
                          {item.stats.reduce(
                            (sum, stat) => sum + stat.metrics.clicks,
                            0
                          )}
                        </div>
                      </div>
                      <div className="rounded-md border p-3">
                        <div className="text-sm text-muted-foreground">
                          Bounces
                        </div>
                        <div className="mt-1 text-lg font-medium">
                          {item.stats.reduce(
                            (sum, stat) => sum + stat.metrics.bounces,
                            0
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              )}
          </div>
        )}
      </main>
    </div>
  );
}
